import React, { useState, useEffect, useRef } from 'react';
import { fmtDate, fmtDatetime, utcToInputTz, inputTzToUTC } from '../../utils/dateUtils';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { ArrowLeft, LifeBuoy, BarChart3, MessageSquare, Save, RefreshCw, Loader2,
  AlertTriangle, Lightbulb, HelpCircle, Sparkles, TrendingUp, ShieldCheck, Search } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

// ── Status / priority colours ──────────────────────────────────────────────
const STATUS_COLORS = {
  open: 'bg-red-100 text-red-700',
  in_progress: 'bg-yellow-100 text-yellow-700',
  resolved: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-600',
};
const PRIORITY_COLORS = {
  high: 'bg-orange-100 text-orange-700',
  normal: 'bg-blue-100 text-blue-700',
  low: 'bg-gray-100 text-gray-600',
};
const SEV_COLORS = { high: 'text-red-600', medium: 'text-yellow-600', low: 'text-green-600' };
const FREQ_COLORS = { high: 'bg-red-100 text-red-700', medium: 'bg-yellow-100 text-yellow-700', low: 'bg-gray-100 text-gray-600' };

const TicketsPanel = () => {
  const qc = useQueryClient();
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [statusFilter, setStatusFilter] = useState('open');
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  const { data: tickets = [], isLoading: loading, refetch: refetchTickets } = useQuery({
    queryKey: QK.supportTickets(statusFilter),
    queryFn: () => api.get(`/chatbot/tickets?status=${statusFilter === 'all' ? '' : statusFilter}`).then(r => r.data),
    onError: () => toast.error('Failed to load tickets'),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: QK.supportTickets(statusFilter) });

  const updateStatus = async (ticketId, status) => {
    try {
      await api.put(`/chatbot/tickets/${ticketId}`, { status });
      toast.success('Ticket updated');
      invalidate();
      if (selectedTicket?.id === ticketId) setSelectedTicket(t => ({ ...t, status }));
    } catch { toast.error('Failed to update'); }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/chatbot/tickets/${selectedTicket.id}`, { internal_notes: notes });
      toast.success('Notes saved');
      setSelectedTicket(t => ({ ...t, internal_notes: notes }));
    } catch { toast.error('Failed to save notes'); }
    setSavingNotes(false);
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <LifeBuoy className="w-5 h-5" /> Support Tickets
          </CardTitle>
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32 h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={refetchTickets} className="h-8">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : tickets.length === 0 ? (
            <p className="text-center text-muted-foreground py-12 text-sm">No tickets found.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Summary</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tickets.map(t => (
                  <TableRow key={t.id} className="cursor-pointer hover:bg-muted/30" onClick={() => { setSelectedTicket(t); setNotes(t.internal_notes || ''); }}>
                    <TableCell className="text-sm font-medium">{t.user_name || t.user_email}</TableCell>
                    <TableCell className="text-sm max-w-xs truncate">{t.summary}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs capitalize">{t.category}</Badge></TableCell>
                    <TableCell><Badge className={`text-xs pointer-events-none ${PRIORITY_COLORS[t.priority] || ''}`}>{t.priority}</Badge></TableCell>
                    <TableCell><Badge className={`text-xs pointer-events-none ${STATUS_COLORS[t.status] || ''}`}>{t.status?.replace('_', ' ')}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtDate(t.created_at)}</TableCell>
                    <TableCell>
                      <Select value={t.status} onValueChange={v => { updateStatus(t.id, v); }} onClick={e => e.stopPropagation()}>
                        <SelectTrigger className="w-28 h-7 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="resolved">Resolved</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Ticket detail dialog */}
      <Dialog open={!!selectedTicket} onOpenChange={o => !o && setSelectedTicket(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              {selectedTicket?.summary}
            </DialogTitle>
          </DialogHeader>
          {selectedTicket && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div><span className="text-muted-foreground">User: </span><span className="font-medium">{selectedTicket.user_name}</span></div>
                <div><span className="text-muted-foreground">Role: </span><span className="font-medium capitalize">{selectedTicket.user_roles?.[0]?.replace('tenant_', '')}</span></div>
                <div><span className="text-muted-foreground">Category: </span><span className="font-medium capitalize">{selectedTicket.category}</span></div>
              </div>
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Conversation</p>
                <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-3 bg-muted/20">
                  {(selectedTicket.conversation || []).map((m, i) => (
                    <div key={i} className={`text-xs p-2 rounded ${m.role === 'user' ? 'bg-white border' : 'bg-primary/10'}`}>
                      <span className="font-semibold capitalize">{m.role}: </span>{m.content}
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Internal Notes</Label>
                <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Add internal notes..." />
                <Button size="sm" onClick={saveNotes} disabled={savingNotes} className="bg-primary hover:bg-primary/90 text-white">
                  {savingNotes ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                  Save Notes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

// ── Analytics panel ────────────────────────────────────────────────────────
const AnalyticsPanel = () => {
  const qc = useQueryClient();
  const [cachedAt, setCachedAt] = useState('');

  const { data, isLoading: loading, refetch } = useQuery({
    queryKey: QK.chatbotAnalytics(),
    queryFn: async () => {
      const res = await api.get('/chatbot/analytics');
      setCachedAt(res.data.cached_at || '');
      return res.data;
    },
  });

  const fetchAnalytics = async (force = false) => {
    if (force) {
      try {
        const res = await api.get('/chatbot/analytics?force=true');
        setCachedAt(res.data.cached_at || '');
        qc.setQueryData(['chatbot-analytics'], res.data);
      } catch { toast.error('Analysis failed'); }
    } else {
      refetch();
    }
  };

  const ins = data?.insights;
  const stats = data?.stats;

  return (
    <div className="space-y-6">
      {/* Stats bar — real chat usage metrics */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: 'Total Messages', value: stats.total_messages ?? stats.total ?? 0, color: 'text-foreground' },
            { label: 'Chat Sessions', value: stats.unique_sessions ?? 0, color: 'text-blue-600' },
            { label: 'Active Tenants', value: stats.active_tenants ?? 0, color: 'text-green-600' },
            { label: 'Escalated to Support', value: stats.escalated_tickets ?? stats.high_priority ?? 0, color: 'text-orange-600' },
          ].map(s => (
            <Card key={s.label}>
              <CardContent className="pt-4 pb-3 text-center">
                <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" /> AI Insights
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Patterns and gaps identified from all chatbot conversations
            {cachedAt && (
              <span className="ml-2 text-[10px] text-muted-foreground/70">
                · Last analysed {fmtDate(cachedAt)}
              </span>
            )}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => fetchAnalytics(true)} disabled={loading} className="gap-1.5">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {loading ? 'Analysing…' : 'Refresh Analysis'}
        </Button>
      </div>

      {loading && !data && (
        <div className="flex items-center justify-center py-16 gap-3 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">Analysing ticket patterns…</span>
        </div>
      )}
      {loading && data && (
        <div className="text-xs text-muted-foreground flex items-center gap-1.5 mb-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Refreshing analysis…
        </div>
      )}

      {ins && (
        <>
          {/* Executive summary */}
          {ins.summary && (
            <Card className="border-primary/20 bg-primary/5">
              <CardContent className="pt-4 pb-3">
                <p className="text-sm leading-relaxed">{ins.summary}</p>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-2 gap-6">
            {/* FAQs */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <HelpCircle className="w-4 h-4 text-blue-500" /> Frequently Asked Questions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {(ins.top_faqs || []).length === 0
                  ? <p className="text-xs text-muted-foreground">Not enough data yet.</p>
                  : ins.top_faqs.map((f, i) => (
                    <div key={i} className="flex items-start justify-between gap-2 py-1.5 border-b last:border-0">
                      <div className="flex items-start gap-2 min-w-0">
                        <span className="text-[10px] font-bold text-muted-foreground mt-0.5 shrink-0">#{i + 1}</span>
                        <div className="min-w-0">
                          <p className="text-xs font-medium leading-snug">{f.question}</p>
                          <p className="text-[10px] text-muted-foreground capitalize">{f.category}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-[10px] shrink-0">{f.count}×</Badge>
                    </div>
                  ))}
              </CardContent>
            </Card>

            {/* Frustration areas */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-500" /> Frustration Signals
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {(ins.frustration_areas || []).length === 0
                  ? <p className="text-xs text-muted-foreground">Not enough data yet.</p>
                  : ins.frustration_areas.map((f, i) => (
                    <div key={i} className="py-1.5 border-b last:border-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-xs font-medium">{f.area}</p>
                        <span className={`text-[10px] font-semibold uppercase ${SEV_COLORS[f.severity] || ''}`}>{f.severity}</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground leading-snug">{f.description}</p>
                    </div>
                  ))}
              </CardContent>
            </Card>

            {/* Onboarding gaps */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-yellow-500" /> Onboarding Gaps
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {(ins.onboarding_gaps || []).length === 0
                  ? <p className="text-xs text-muted-foreground">Not enough data yet.</p>
                  : ins.onboarding_gaps.map((g, i) => (
                    <div key={i} className="py-1.5 border-b last:border-0">
                      <p className="text-xs font-medium">{g.gap}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5 leading-snug">
                        <span className="font-semibold">Suggestion: </span>{g.suggestion}
                      </p>
                    </div>
                  ))}
              </CardContent>
            </Card>

            {/* Feature requests */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-purple-500" /> Feature Requests
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {(ins.feature_requests || []).length === 0
                  ? <p className="text-xs text-muted-foreground">Not enough data yet.</p>
                  : ins.feature_requests.map((f, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 border-b last:border-0">
                      <p className="text-xs font-medium">{f.feature}</p>
                      <Badge className={`text-[10px] pointer-events-none ${FREQ_COLORS[f.frequency] || ''}`}>{f.frequency}</Badge>
                    </div>
                  ))}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

const ExtensionsPanel = () => {
  const { userTimezone } = useAuth();
  const qc = useQueryClient();
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ extension_deadline: '', extension_reason: '' });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterTenant, setFilterTenant] = useState('all');
  const [filterApprover, setFilterApprover] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortField, setSortField] = useState('extension_granted_at');
  const [sortDir, setSortDir] = useState('desc');

  const editDeadlineRef = useRef(null);
  useEffect(() => {
    if (editDeadlineRef.current) {
      editForm.extension_deadline
        ? editDeadlineRef.current.classList.add('date-filled')
        : editDeadlineRef.current.classList.remove('date-filled');
    }
  }, [editForm.extension_deadline]);

  const { data: extensions = [], isLoading: loading, refetch: refetchExtensions } = useQuery({
    queryKey: QK.saleExtensions(),
    queryFn: () => api.get('/sale-opportunities/extensions/list').then(r => r.data),
  });
  const fetchExtensions = refetchExtensions;
  const invalidateExtensions = () => qc.invalidateQueries({ queryKey: QK.saleExtensions() });

  const handleSaveEdit = async (id) => {
    if (!editForm.extension_deadline || !editForm.extension_reason.trim()) {
      toast.error('Both fields are required'); return;
    }
    setSaving(true);
    try {
      await api.post(`/sale-opportunities/${id}/extension`, {
        extension_deadline: inputTzToUTC(editForm.extension_deadline, userTimezone),
        extension_reason: editForm.extension_reason.trim(),
      });
      toast.success('Extension updated');
      setEditingId(null);
      invalidateExtensions();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to update'); }
    setSaving(false);
  };

  const handleRevoke = async (id) => {
    try {
      await api.delete(`/sale-opportunities/${id}/extension`);
      toast.success('Extension revoked');
      invalidateExtensions();
    } catch { toast.error('Failed to revoke extension'); }
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const SortIcon = ({ field }) => sortField !== field ? null : (
    <span className="ml-1 text-[10px]">{sortDir === 'asc' ? '▲' : '▼'}</span>
  );

  // Derived filter options
  const tenants   = [...new Set(extensions.map(e => e.tenant_name).filter(Boolean))].sort();
  const approvers = [...new Set(extensions.map(e => e.approver_name || e.approver_email).filter(Boolean))].sort();

  // Apply search + filters
  const filtered = extensions
    .filter(e => {
      const q = search.toLowerCase();
      if (q && ![e.opportunity_name, e.tenant_name, e.sale_name, e.extension_reason, e.approver_name, e.approver_email, e.hip_number]
        .some(f => String(f || '').toLowerCase().includes(q))) return false;
      if (filterTenant !== 'all' && e.tenant_name !== filterTenant) return false;
      if (filterApprover !== 'all' && (e.approver_name || e.approver_email) !== filterApprover) return false;
      if (filterStatus === 'active'  &&  e.is_expired) return false;
      if (filterStatus === 'expired' && !e.is_expired) return false;
      return true;
    })
    .sort((a, b) => {
      let av = a[sortField] || '', bv = b[sortField] || '';
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">Sale Entry Extensions</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Confirmation deadline exceptions granted by System Owners
            {!loading && <> · <strong>{filtered.length}</strong> of {extensions.length}</>}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchExtensions} disabled={loading}>
          <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Search + filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            className="pl-8 h-8 text-sm"
            placeholder="Search opportunity, tenant, reason…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="h-8 text-xs w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active only</SelectItem>
            <SelectItem value="expired">Expired only</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterTenant} onValueChange={setFilterTenant}>
          <SelectTrigger className="h-8 text-xs w-44"><SelectValue placeholder="All tenants" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tenants</SelectItem>
            {tenants.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterApprover} onValueChange={setFilterApprover}>
          <SelectTrigger className="h-8 text-xs w-44"><SelectValue placeholder="All approvers" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All approvers</SelectItem>
            {approvers.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        {(search || filterStatus !== 'all' || filterTenant !== 'all' || filterApprover !== 'all') && (
          <Button variant="ghost" size="sm" className="h-8 text-xs text-muted-foreground"
            onClick={() => { setSearch(''); setFilterStatus('all'); setFilterTenant('all'); setFilterApprover('all'); }}>
            Clear filters
          </Button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground text-sm">
          {extensions.length === 0 ? 'No extensions have been granted.' : 'No extensions match your filters.'}
        </CardContent></Card>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead className="text-xs cursor-pointer select-none w-44" onClick={() => toggleSort('opportunity_name')}>
                  Opportunity <SortIcon field="opportunity_name" />
                </TableHead>
                <TableHead className="text-xs cursor-pointer select-none" onClick={() => toggleSort('tenant_name')}>
                  Tenant <SortIcon field="tenant_name" />
                </TableHead>
                <TableHead className="text-xs cursor-pointer select-none" onClick={() => toggleSort('sale_name')}>
                  Sale <SortIcon field="sale_name" />
                </TableHead>
                <TableHead className="text-xs cursor-pointer select-none" onClick={() => toggleSort('extension_deadline')}>
                  Expires <SortIcon field="extension_deadline" />
                </TableHead>
                <TableHead className="text-xs cursor-pointer select-none" onClick={() => toggleSort('extension_granted_at')}>
                  Granted <SortIcon field="extension_granted_at" />
                </TableHead>
                <TableHead className="text-xs cursor-pointer select-none" onClick={() => toggleSort('approver_name')}>
                  Approved By <SortIcon field="approver_name" />
                </TableHead>
                <TableHead className="text-xs">Reason</TableHead>
                <TableHead className="text-xs w-28">Status</TableHead>
                <TableHead className="text-xs w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(ext => {
                const isEditing = editingId === ext.id;
                return (
                  <React.Fragment key={ext.id}>
                    <TableRow className={`text-xs ${ext.is_expired ? 'bg-red-50/40' : ''}`}>
                      <TableCell className="font-medium">
                        {ext.opportunity_name || '—'}
                        {ext.hip_number && <span className="text-muted-foreground ml-1">#{ext.hip_number}</span>}
                      </TableCell>
                      <TableCell>{ext.tenant_name || '—'}</TableCell>
                      <TableCell>{ext.sale_name || '—'}</TableCell>
                      <TableCell className={ext.is_expired ? 'text-red-600 font-medium' : 'text-amber-700 font-medium'}>
                        {fmtDatetime(ext.extension_deadline, userTimezone)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {ext.extension_granted_at ? fmtDate(ext.extension_granted_at) : '—'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{ext.approver_name || ext.approver_email || '—'}</TableCell>
                      <TableCell className="max-w-xs truncate text-muted-foreground" title={ext.extension_reason}>
                        {ext.extension_reason}
                      </TableCell>
                      <TableCell>
                        <Badge className={ext.is_expired ? 'bg-red-100 text-red-700 text-[10px]' : 'bg-amber-100 text-amber-700 text-[10px]'}>
                          {ext.is_expired ? 'Expired' : 'Active'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs"
                            onClick={() => {
                              setEditForm({ extension_deadline: utcToInputTz(ext.extension_deadline, userTimezone), extension_reason: ext.extension_reason || '' });
                              setEditingId(isEditing ? null : ext.id);
                            }}>
                            {isEditing ? 'Cancel' : 'Edit'}
                          </Button>
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-red-500 hover:text-red-700"
                            onClick={() => handleRevoke(ext.id)}>
                            Revoke
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {/* Inline edit row */}
                    {isEditing && (
                      <TableRow className="bg-amber-50/60">
                        <TableCell colSpan={9} className="py-3 px-4">
                          <div className="flex flex-wrap gap-3 items-end">
                            <div className="space-y-1">
                              <Label className="text-[10px] text-muted-foreground">New Expiry Date/Time *</Label>
                              <Input ref={editDeadlineRef} type="datetime-local" className="h-7 text-xs w-52"
                                value={editForm.extension_deadline}
                                onChange={e => setEditForm(f => ({ ...f, extension_deadline: e.target.value }))} />
                            </div>
                            <div className="space-y-1 flex-1 min-w-64">
                              <Label className="text-[10px] text-muted-foreground">Reason *</Label>
                              <Input className="h-7 text-xs" value={editForm.extension_reason}
                                onChange={e => setEditForm(f => ({ ...f, extension_reason: e.target.value }))} />
                            </div>
                            <Button size="sm" className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                              onClick={() => handleSaveEdit(ext.id)} disabled={saving}>
                              {saving ? 'Saving…' : 'Save'}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
};

// ── Main page ──────────────────────────────────────────────────────────────
const SupportCenterPage = () => {
  const navigate = useNavigate();
  const { userTimezone } = useAuth();
  return (
    <div className="min-h-screen bg-background">
      <header className="bg-primary text-white px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/system')} className="text-white hover:bg-white/20">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-serif text-xl font-semibold">Support Center</h1>
            <p className="text-xs text-white/70">Support tickets, AI-powered analytics, and entry extensions</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        <Tabs defaultValue="tickets" className="space-y-6">
          <TabsList>
            <TabsTrigger value="tickets">
              <LifeBuoy className="w-4 h-4 mr-2" /> Support Tickets
            </TabsTrigger>
            <TabsTrigger value="analytics">
              <BarChart3 className="w-4 h-4 mr-2" /> Chat Analytics
            </TabsTrigger>
            <TabsTrigger value="extensions">
              <ShieldCheck className="w-4 h-4 mr-2" /> Extensions
            </TabsTrigger>
          </TabsList>
          <TabsContent value="tickets"><TicketsPanel /></TabsContent>
          <TabsContent value="analytics"><AnalyticsPanel /></TabsContent>
          <TabsContent value="extensions"><ExtensionsPanel /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default SupportCenterPage;
